// SuperChart.js
// A combined chart: a titled stack of child charts, each drawn by its own
// renderer on its own card. Its export button saves the WHOLE chart as one
// image — every leaf tiled under this title — while each child keeps its own
// single-chart export, titled with its ancestry.
import React from 'react';
import { useTheme } from '@mui/material/styles';
import ChartCard from './ChartCard';
import ChartExportButton from './ChartExportButton';
import BarChartComponent from './BarChart';
import LineChartComponent from './LineChart';
import PieChartComponent from './PieChart';
import BoxAndWhiskerChartComponent from './BoxAndWhiskerChart';
import ScatterChartComponent from './ScatterChart';
import { buildChartFigure, flattenChartLeaves } from './chartFigures';
import { downloadCompositePng, exportTitle } from '../../utils/chartExport';

const CHILD_COMPONENTS = {
  bar: BarChartComponent,
  line: LineChartComponent,
  pie: PieChartComponent,
  box_and_whisker: BoxAndWhiskerChartComponent,
  scatter: ScatterChartComponent,
};

function SuperChartComponent({ chartData, showTitle = true, ancestorTitles = [] }) {
  const theme = useTheme();
  const ink = { primary: theme.palette.text.primary, secondary: theme.palette.text.secondary };
  const themePalette = theme.palette.custom.chartColors;
  const ownTitle = typeof chartData.title === 'string' && chartData.title.trim() ? chartData.title.trim() : null;
  // What the children print as their prefix: this chart's ancestry plus itself.
  const childAncestors = ownTitle ? [...ancestorTitles, ownTitle] : ancestorTitles;

  const exportAll = () => downloadCompositePng({
    title: exportTitle(ancestorTitles, chartData.title),
    leaves: flattenChartLeaves(chartData).map(({ chart, ancestorTitles: between }) => ({
      ...buildChartFigure(chart, { ink, themePalette }),
      title: exportTitle(between, chart.title),
    })),
  });

  const renderChild = (child) => {
    if (child?.type === 'superchart') {
      return <SuperChartComponent chartData={child} ancestorTitles={childAncestors} />;
    }
    const Component = CHILD_COMPONENTS[child?.type];
    return Component ? <Component chartData={child} ancestorTitles={childAncestors} /> : null;
  };

  return (
    <ChartCard
      title={showTitle ? chartData.title : null}
      ink={ink}
      actions={<ChartExportButton onExport={exportAll} />}
    >
      <div>
        {chartData.elements.map((child, index) => (
          <div key={index} style={{ marginBottom: '20px', height: '40vh', minHeight: '300px' }}>
            {renderChild(child)}
          </div>
        ))}
      </div>
    </ChartCard>
  );
}

export default SuperChartComponent;
